import React, { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Play,
  Video,
  Tv,
  Music2,
  Sparkles,
  Eye,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import NarrativeSection from "../../../NarrativeSection";
import DiscoveryVideoGrid from "../DiscoveryVideoGrid";

/**
 * DynamicBrandVideosSection Component
 * 
 * A discovery section specifically for showcasing brand videos with tab-based filtering
 * and enhanced presentation. Integrates with the existing dynamic profile system.
 */
const DynamicBrandVideosSection = ({
  videos = [],
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  title = "Brand Videos",
  description = "",
  showCategories = true,
  maxColumns = 3,
  enableModal = true,
}) => {
  const sectionRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.8, 1, 1, 0.8]);

  // Intersection observer for visibility-based animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Background image with parallax effect
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  // Get statistics about videos
  const getVideoStats = () => {
    if (!videos || videos.length === 0) return null;
    
    const categories = new Set();
    videos.forEach(video => {
      if (Array.isArray(video.categories)) {
        video.categories.forEach(cat => categories.add(cat));
      } else if (video.category) {
        categories.add(video.category);
      }
    });

    return {
      total: videos.length,
      categories: categories.size,
      hasDescriptions: videos.filter(v => v.description).length,
    };
  };

  const stats = getVideoStats();

  if (!videos || videos.length === 0) {
    return (
      <section
        ref={sectionRef}
        className="min-h-screen relative flex items-center justify-center py-20 overflow-hidden"
        style={{
          backgroundColor: "rgba(17, 17, 17, 0.8)",
          color: "rgba(255, 255, 255, 0.9)",
        }}
      >
        {/* Background with parallax */}
        {backgroundImage && (
          <motion.div
            className="absolute inset-0 w-full h-full bg-center bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              y: backgroundY,
              opacity: 0.3,
              filter: "contrast(1.1) brightness(1.05)",
              mixBlendMode: "soft-light",
            }}
          />
        )}

        {/* Gradient overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, 
              rgba(var(--primary-color-rgb), 0.1) 0%, 
              transparent 50%, 
              rgba(var(--secondary-color-rgb), 0.1) 100%)`,
          }}
        />

        <motion.div
          style={{ opacity, scale }}
          className="relative z-10 w-full max-w-6xl mx-auto px-6 text-center"
        >
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-12 border border-white/10">
            <div className="w-20 h-20 mx-auto mb-6 bg-white/10 rounded-full flex items-center justify-center">
              <Video size={32} className="text-white/50" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {title}
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Brand videos will be showcased here once they're added to the profile. 
              Videos can be categorized and filtered for easy browsing.
            </p>
          </div>
        </motion.div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-20 overflow-hidden"
      style={{
        backgroundColor: "rgba(17, 17, 17, 0.8)",
        color: "rgba(255, 255, 255, 0.9)",
      }}
    >
      {/* Background with parallax */}
      {backgroundImage && (
        <motion.div
          className="absolute inset-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            y: backgroundY,
            opacity: 0.4,
            filter: "contrast(1.1) brightness(1.05)",
            mixBlendMode: "soft-light",
          }}
        />
      )}

      {/* Gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, 
            rgba(var(--primary-color-rgb), 0.15) 0%, 
            transparent 50%, 
            rgba(var(--secondary-color-rgb), 0.15) 100%)`,
        }}
      />

      {/* Dot pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgba(255,255,255,0.3)_1px,_transparent_0)] [background-size:50px_50px]" />
      </div>

      <motion.div
        style={{ opacity, scale }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[var(--primary-color)]/20 rounded-full flex items-center justify-center">
              <Tv size={24} style={{ color: "var(--primary-color)" }} />
            </div>
            <motion.div
              className="h-px flex-1 max-w-32"
              style={{
                background: `linear-gradient(90deg, transparent 0%, var(--primary-color) 50%, transparent 100%)`,
              }}
              initial={{ scaleX: 0 }}
              animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            />
            <Sparkles size={20} style={{ color: "var(--accent-color)" }} />
            <motion.div
              className="h-px flex-1 max-w-32"
              style={{
                background: `linear-gradient(90deg, transparent 0%, var(--primary-color) 50%, transparent 100%)`,
              }}
              initial={{ scaleX: 0 }}
              animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            />
          </div>

          <h2
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-transparent bg-clip-text"
            style={{
              backgroundImage: `linear-gradient(135deg, 
                var(--primary-color) 0%, 
                var(--secondary-color) 50%, 
                var(--accent-color) 100%)`,
            }}
          >
            {title}
          </h2>

          {description && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed"
            >
              {description}
            </motion.p>
          )}

          {/* Video Statistics */}
          {stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex items-center justify-center gap-8 mt-8 text-white/60"
            >
              <div className="flex items-center gap-2">
                <Play size={16} />
                <span>{stats.total} video{stats.total !== 1 ? 's' : ''}</span>
              </div>
              {stats.categories > 1 && (
                <div className="flex items-center gap-2">
                  <Eye size={16} />
                  <span>{stats.categories} categories</span>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Video Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative"
        >
          {/* Background for video grid */}
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm rounded-3xl border border-white/10 shadow-2xl" />
          
          <div className="relative z-10 p-8 md:p-12">
            <DiscoveryVideoGrid
              videos={videos}
              title=""
              showCategories={showCategories}
              defaultCategory="all"
              maxColumns={maxColumns}
              enableModal={enableModal}
              className="space-y-8"
            />
          </div>
        </motion.div>

        {/* Narrative Section Integration */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-16"
          >
            <NarrativeSection
              sectionId={`brand-videos-${sectionIndex}`}
              profileId={profileId}
              defaultContent="Explore our collection of brand videos showcasing the essence and story of this brand through compelling visual narratives."
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            />
          </motion.div>
        )}

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="flex justify-center mt-16"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-white/50 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default DynamicBrandVideosSection;